export default (element: HTMLElement) => {
  const selectElement = element.querySelector<HTMLSelectElement>('select');
  const allowWideSearch = element.dataset.allowWideSearch === 'true';
  const onlyInsideMyUnit = element.dataset.onlyInsideMyUnit === 'true';
  const allowedUnitTypes = element.dataset.allowedUnitTypes
    ? (JSON.parse(element.dataset.allowedUnitTypes) as string[])
    : null;

  if (!selectElement) {
    console.error('Missing select element in selectUnit component');
    return;
  }

  const select = selectElement; // Now TypeScript knows this is non-null

  // Create search input overlay
  const searchContainer = document.createElement('div');
  searchContainer.className = 'selectUnit__searchContainer';

  const inputWrapper = document.createElement('div');
  inputWrapper.className = 'selectUnit__inputWrapper';

  const searchInput = document.createElement('input');
  searchInput.type = 'text';
  searchInput.className = 'selectUnit__searchInput';
  searchInput.placeholder = allowWideSearch
    ? 'Hledat jednotku...'
    : 'Filtrovat jednotky...';

  const icon = document.createElement('i');
  icon.setAttribute('data-lucide', 'chevron-down');
  icon.className = 'selectUnit__icon';

  inputWrapper.appendChild(searchInput);
  inputWrapper.appendChild(icon);

  const dropdown = document.createElement('div');
  dropdown.className = 'selectUnit__dropdown';
  const supportsPopover = 'popover' in HTMLElement.prototype;
  if (supportsPopover) {
    dropdown.popover = 'manual';
    dropdown.style.position = 'fixed';
  } else {
    dropdown.style.display = 'none';
  }

  searchContainer.appendChild(inputWrapper);
  searchContainer.appendChild(dropdown);

  // Insert search container before select
  select.parentElement?.insertBefore(searchContainer, select);
  select.style.display = 'none';

  // Store original options for local filtering
  const originalOptions: { value: string; text: string }[] = [];
  Array.from(select.options).forEach((option) => {
    if (option.value) {
      originalOptions.push({
        value: option.value,
        text: option.textContent || '',
      });
    }
  });

  let isOpen = false;
  let selectedIndex = -1;
  let debounceTimeout: number | null = null;
  let abortController: AbortController | null = null;

  // Position dropdown for popover mode
  function positionDropdown() {
    if (supportsPopover) {
      const rect = inputWrapper.getBoundingClientRect();
      dropdown.style.top = `${rect.bottom + 4}px`;
      dropdown.style.left = `${rect.left}px`;
      dropdown.style.width = `${rect.width}px`;
    }
  }

  // Create dropdown options
  function createOption(
    value: string,
    text: string,
    isSelected: boolean = false,
  ): HTMLDivElement {
    const option = document.createElement('div');
    option.className = 'selectUnit__option';
    option.textContent = text;
    option.dataset.value = value;
    if (isSelected) {
      option.classList.add('selectUnit__option--selected');
    }

    option.addEventListener('click', () => {
      selectOption(value, text);
    });

    return option;
  }

  // Select an option
  function selectOption(value: string, text: string) {
    // Check if option exists in select element
    let optionExists = false;
    for (let i = 0; i < select.options.length; i++) {
      if (select.options[i].value === value) {
        optionExists = true;
        break;
      }
    }

    // If option doesn't exist (e.g., from API search), add it to the select
    if (!optionExists) {
      const newOption = document.createElement('option');
      newOption.value = value;
      newOption.textContent = text;
      select.appendChild(newOption);
    }

    // Set the value
    select.value = value;
    searchInput.value = text;
    closeDropdown();

    // Clear any validation errors since a valid option is now selected
    searchInput.setCustomValidity('');

    // Trigger change event on select
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Open dropdown
  function openDropdown() {
    if (!isOpen) {
      isOpen = true;
      positionDropdown();
      try {
        dropdown.showPopover();
      } catch (_error) {
        // Fallback for browsers without Popover API
        dropdown.style.display = 'block';
      }
      // Clear search input to show all options
      const previousValue = searchInput.value;
      searchInput.value = '';
      searchInput.focus();
      updateDropdown();
      // Select all so user can start typing
      searchInput.value = previousValue;
      searchInput.select();
    }
  }

  // Close dropdown
  function closeDropdown() {
    isOpen = false;
    try {
      dropdown.hidePopover();
    } catch (_error) {
      // Fallback for browsers without Popover API
      dropdown.style.display = 'none';
    }
    selectedIndex = -1;
  }

  // Filter local options
  function filterLocalOptions(
    query: string,
  ): { value: string; text: string }[] {
    const lowerQuery = query.toLowerCase();
    return originalOptions.filter((option) =>
      option.text.toLowerCase().includes(lowerQuery),
    );
  }

  // Search via API
  async function searchAPI(query: string) {
    if (query.trim().length === 0) {
      updateDropdown();
      return;
    }

    dropdown.innerHTML = '<div class="selectUnit__loading">Hledám...</div>';

    try {
      const allowedTypesParam = allowedUnitTypes
        ? `&allowed_unit_types=${allowedUnitTypes.map((ut) => encodeURIComponent(ut)).join(',')}`
        : '';
      const insideMyUnitParam = onlyInsideMyUnit
        ? '&only_inside_my_unit=true'
        : '';
      abortController?.abort();
      const localAbortController = new AbortController();
      abortController = localAbortController;
      let response = null;
      try {
        response = await fetch(
          `/api/search-unit?query=${encodeURIComponent(query)}${allowedTypesParam}${insideMyUnitParam}`,
          { signal: localAbortController.signal },
        );
      } catch (error) {
        if ((error as DOMException).name === 'AbortError') {
          return; // Request was aborted, exit silently
        }
        throw error;
      }

      if (localAbortController.signal.aborted) {
        return; // If aborted after fetch, exit silently
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const units: {
        id: number;
        display_name: string;
        registration_number: string;
      }[] = await response.json();

      dropdown.innerHTML = '';

      if (units.length === 0) {
        const noResults = document.createElement('div');
        noResults.className = 'selectUnit__noResults';
        noResults.textContent = 'Žádné jednotky nenalezeny';
        dropdown.appendChild(noResults);
      } else {
        units.forEach((unit) => {
          const optionText = `${unit.registration_number} - ${unit.display_name}`;
          const option = createOption(
            unit.id.toString(),
            optionText,
            select.value === unit.id.toString(),
          );
          dropdown.appendChild(option);
        });
      }
    } catch (error) {
      console.error('Error searching units:', error);
      dropdown.innerHTML =
        '<div class="selectUnit__error">Chyba při hledání jednotek</div>';
    }
  }

  // Update dropdown based on mode
  function updateDropdown() {
    const query = searchInput.value.trim();

    if (debounceTimeout !== null) {
      clearTimeout(debounceTimeout);
    }
    abortController?.abort();
    abortController = null;
    if (allowWideSearch && query.length > 0) {
      // Use API search when user types
      debounceTimeout = window.setTimeout(() => {
        searchAPI(query);
        debounceTimeout = null;
      }, 300);
    } else {
      // Local filtering
      const options = filterLocalOptions(query);
      dropdown.innerHTML = '';

      if (options.length === 0) {
        const noResults = document.createElement('div');
        noResults.className = 'selectUnit__noResults';
        noResults.textContent =
          query.length === 0 && allowWideSearch
            ? 'Začněte psát pro vyhledání jednotky'
            : 'Žádné jednotky nenalezeny';
        dropdown.appendChild(noResults);
      } else {
        options.forEach((option) => {
          const optionElement = createOption(
            option.value,
            option.text,
            select.value === option.value,
          );
          dropdown.appendChild(optionElement);
        });
      }
    }

    selectedIndex = -1;
  }

  // Handle keyboard navigation
  function handleKeyDown(e: KeyboardEvent) {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        e.preventDefault();
        openDropdown();
      }
      return;
    }

    const options = Array.from(
      dropdown.querySelectorAll<HTMLDivElement>('.selectUnit__option'),
    );

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (options.length > 0) {
          selectedIndex = (selectedIndex + 1) % options.length;
          updateSelectedVisual(options);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (options.length > 0) {
          selectedIndex =
            selectedIndex <= 0 ? options.length - 1 : selectedIndex - 1;
          updateSelectedVisual(options);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && options[selectedIndex]) {
          const value = options[selectedIndex].dataset.value || '';
          const text = options[selectedIndex].textContent || '';
          selectOption(value, text);
        }
        break;
      case 'Escape':
        e.preventDefault();
        closeDropdown();
        searchInput.blur();
        break;
      case 'Tab':
        closeDropdown();
        break;
    }
  }

  // Update visual highlight for keyboard navigation
  function updateSelectedVisual(options: HTMLDivElement[]) {
    options.forEach((option, index) => {
      option.classList.toggle(
        'selectUnit__option--highlighted',
        index === selectedIndex,
      );
    });

    // Scroll highlighted option into view
    if (selectedIndex >= 0 && options[selectedIndex]) {
      options[selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  // Set initial value if select has a value
  if (select.value) {
    const selectedOption = select.options[select.selectedIndex];
    if (selectedOption) {
      searchInput.value = selectedOption.textContent || '';
    }
  }

  // Event listeners
  inputWrapper.addEventListener('click', () => {
    if (!isOpen) {
      openDropdown();
    }
  });

  searchInput.addEventListener('focus', () => {
    if (!isOpen) {
      openDropdown();
    }
  });

  searchInput.addEventListener('input', () => {
    if (isOpen) {
      searchInput.setCustomValidity('');
      updateDropdown();
    }
  });

  searchInput.addEventListener('keydown', handleKeyDown);

  // Click outside to close
  document.addEventListener('click', (e) => {
    if (!element.contains(e.target as Node)) {
      closeDropdown();
    }
  });

  // Handle select changes from outside (e.g., form reset)
  select.addEventListener('change', () => {
    const selectedOption = select.options[select.selectedIndex];
    if (selectedOption?.value) {
      searchInput.value = selectedOption.textContent || '';
    } else {
      searchInput.value = '';
    }
  });

  // Reposition dropdown on scroll/resize (only for popover mode)
  if (supportsPopover) {
    window.addEventListener(
      'scroll',
      () => {
        if (isOpen) {
          positionDropdown();
        }
      },
      true,
    );
    window.addEventListener('resize', () => {
      if (isOpen) {
        positionDropdown();
      }
    });
  }

  // Listen to validity changes to show message
  select.addEventListener('invalid', (_event) => {
    const message = select.validationMessage;
    searchInput.setCustomValidity(message);
  });
};
